import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AppStateFailureBuilder, AppStateFailureItemWithErrorCode, FailureType } from '@symphony-solo/shared/common-state/src';
import { ApiResponseCode } from '@symphony-solo/shared/http/src';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { OrderService } from '../services';
import {
  AutoUpdateTimeSlotIfExpiredAction,
  checkOrderStatusAction,
  checkTopUpStatusAction,
  ClearStateAction,
  DiscountAction,
  DiscountErrorAction,
  DiscountSuccessAction,
  GuestCheckoutDiscountErrorAction,
  GuestCheckoutDiscountSuccessAction,
  OrderCompleteAction,
  OrderCompleteErrorAction,
  OrderCompleteSuccessAction,
  OrderDetailsAction,
  OrderDetailsErrorAction,
  OrderDetailsSuccessAction,
  SessionCreateAction,
  SessionCreateErrorAction,
  SessionCreateGuestCheckoutAction,
  SessionCreateGuestCheckoutErrorAction,
  SessionCreateSuccessAction,
  SessionRetrieveAction,
  SessionRetrieveErrorAction,
  SessionRetrieveGuestCheckoutAction,
  SessionRetrieveSuccessAction,
  showGuestCheckoutAlert,
  VerifyOrderAction,
  VerifyOrderErrorAction,
  VerifyOrderSuccessAction,
} from './order.actions';
import { ModalController, NavController } from '@ionic/angular';
import { CHECKOUT_ORDER, LOYALTY_WALLET_FULL_TABS, PAYMENT_SUCCESS } from '@symphony-solo/shared/utils/src';
import { CompanyConfigFacade } from '@symphony-solo/domain/company-config/src/lib/+state/company-config.facade';
import { alerts, AlertService } from '@symphony-solo/shared/alert/src';
import { AuthenticationFacade } from '@symphony-solo/domain/authentication/src/lib/+state/authentication.facade';
import { OrderFacade } from './order.facade';
import { OrderInfoBuilder } from '../builders';
import { Delivery } from '../models';
import { CartFacade } from '@symphony-solo/domain/cart/src/lib/+state/cart.facade';
import {
  ChooseLocationFacade,
  ChooseLocationService,
  getLocationTimeslotErrorAction,
  TimeslotSelection,
} from '@symphony-solo/domain/choose-location/src';
import { VoucherFacade } from '@symphony-solo/domain/voucher/src';
import { PursesFacade } from '@symphony-solo/domain/purses/src';
import { HttpResponse } from '@angular/common/http';
import { WalletInterrogateSuccessPageComponent } from '@symphony-solo/pages/loyalty/wallet-interrogate-success/src/lib/wallet-interrogate-success/wallet-interrogate-success-page.component';

@Injectable()
export class OrderEffects {
  constructor(
    private action$: Actions,
    private orderService: OrderService,
    private orderFacade: OrderFacade,
    private navCtrl: NavController,
    private companyConfigFacade: CompanyConfigFacade,
    private alertService: AlertService,
    private authenticationFacade: AuthenticationFacade,
    private cartFacade: CartFacade,
    private chooseLocationFacade: ChooseLocationFacade,
    private chooseLocationService: ChooseLocationService,
    private voucherFacade: VoucherFacade,
    private pursesFacade: PursesFacade,
    public modalCtrl: ModalController
  ) {}

  protected verifyOrder$ = createEffect(() =>
    this.action$.pipe(
      ofType(VerifyOrderAction),
      withLatestFrom(this.authenticationFacade.isAuthenticated$),
      switchMap(([action, isAuthenticated]) => {
        if (isAuthenticated) {
          return this.orderService.verifyOrder(action).pipe(
            map(() => VerifyOrderSuccessAction()),
            catchError((res) => {
              let message = res?.error;

              if (res?.error && res?.error === 'InvalidTimeslot') {
                message = 'invalid time slot';
                this.chooseLocationFacade.setTimeslotExpired(true);
              }

              if (res?.error?.errors) {
                message = 'server error';
              }

              const failure = new AppStateFailureBuilder()
                .setFailureType(FailureType.API)
                .addFailure(
                  new AppStateFailureItemWithErrorCode(
                    'Error on order verifications due to ' + (message ? message : 'something went wrong'),
                    ApiResponseCode.Server_Error
                  )
                )
                .build();
              return of(VerifyOrderErrorAction({ failure }));
            })
          );
        }
        return of(VerifyOrderSuccessAction());
      })
    )
  );

  protected applyDiscount$ = createEffect(() =>
    this.action$.pipe(
      ofType(DiscountAction),
      withLatestFrom(
        this.authenticationFacade.isAuthenticated$,
        this.authenticationFacade.accountDetails$,
        this.companyConfigFacade.checkoutAsGuestSharedAccountId$,
        this.orderFacade.guestCheckoutOrderID$,
        this.orderFacade.discountTotal$,
        this.orderFacade.getDiscount$,
        this.chooseLocationFacade.selectedLocation$,
        this.chooseLocationFacade.selectedTimeslot$,
        this.cartFacade.cart$,
        this.cartFacade.accountID$,
        this.cartFacade.subTotal$,
        this.cartFacade.total$,
        this.orderFacade.customerInfo$,
        this.voucherFacade.loyaltyVouchersToApply$,
        this.pursesFacade.purseID$,
        this.companyConfigFacade.isCustomerInfoOptional$,
        this.orderFacade.getPaymentSessionCreate$
      ),
      switchMap(
        ([
          action,
          isAuthenticated,
          accountDetails,
          checkoutAsGuestSharedAccountId,
          guestCheckoutOrderID,
          discountTotal,
          discounts,
          selectedLocation,
          timeSlot,
          cart,
          cartAccountID,
          subTotal,
          total,
          customerInfo,
          loyaltyVouchersToApply,
          purseID,
          isCustomerInfoOptional,
          paymentSession,
        ]) => {
          if (paymentSession) {
            return of(ClearStateAction({ actionType: 'isApplyingDiscount' })); // Hide SK loading
          }

          let accountID = isAuthenticated ? cartAccountID : checkoutAsGuestSharedAccountId;
          if (isAuthenticated && (!cartAccountID || cartAccountID === -1)) {
            accountID = accountDetails.accountID;
            this.cartFacade.setAccountID(accountID);
          }

          const grandTotal = (+subTotal - +discountTotal).toFixed(2);

          const order = new OrderInfoBuilder()
            .setAccountID(accountID)
            .setLocationID(selectedLocation.id)
            .setPeriodID(timeSlot.periodID)
            .setTimeslotStart(timeSlot.start)
            .setTimeslotEnd(timeSlot.end)
            .setSubTotal(subTotal)
            .setTotal(+grandTotal)
            .setItems(cart)
            .setNotes('')
            .setFirstName(customerInfo?.firstName ? customerInfo.firstName : '-')
            .setLastName(customerInfo?.lastName ? customerInfo.lastName : '-')
            .setEmail(customerInfo?.email ? customerInfo.email : '-')
            .setPhoneNumber(customerInfo?.phone ? customerInfo.phone : '-')
            .setOrderID(guestCheckoutOrderID ? guestCheckoutOrderID : 0)
            .setDiscounts(discounts)
            .build();

          if (isAuthenticated) {
            return this.orderService.applyDiscount({ loyaltyVouchersToApply, order }).pipe(
              map((res: HttpResponse<any>) => {
                this.orderFacade.setOrderID(Number(res.headers.get('x-orderid')));
                return DiscountSuccessAction({ discountResponse: res.body });
              }),
              catchError((res) => {
                let message = res?.error;
                if (res?.error && res?.error === 'InvalidTimeslot') {
                  message = 'invalid time slot';
                  this.chooseLocationFacade.setTimeslotExpired(true);
                }

                if (res?.error?.errors) {
                  message = 'server error';
                }
                const failure = new AppStateFailureBuilder()
                  .setFailureType(FailureType.API)
                  .addFailure(
                    new AppStateFailureItemWithErrorCode(
                      'Error on discount calculation due to ' + (message ? message : 'something went wrong'),
                      ApiResponseCode.Server_Error
                    )
                  )
                  .build();
                return of(DiscountErrorAction({ failure }));
              })
            );
          }

          return this.orderService.applyGuestCheckoutDiscount(order).pipe(
            map((res) => {
              return GuestCheckoutDiscountSuccessAction({ discountResponse: res, createSessionAction: action.createSessionAction });
            }),
            catchError((res) => {
              let message = res?.error;

              if (res?.error && res?.error === 'InvalidTimeslot') {
                message = 'invalid time slot';
                this.chooseLocationFacade.setTimeslotExpired(true);
              }

              if (res?.error?.errors) {
                message = 'server error';
              }

              const failure = new AppStateFailureBuilder()
                .setFailureType(FailureType.API)
                .addFailure(
                  new AppStateFailureItemWithErrorCode(
                    'Error on discount calculation due to ' + (message ? message : 'something went wrong'),
                    ApiResponseCode.Server_Error
                  )
                )
                .build();
              return of(GuestCheckoutDiscountErrorAction({ failure }));
            })
          );
        }
      )
    )
  );

  protected guestCheckoutDiscountSuccess$ = createEffect(() =>
    this.action$.pipe(
      ofType(GuestCheckoutDiscountSuccessAction),
      withLatestFrom(
        this.orderFacade.discountTotal$,
        this.cartFacade.subTotal$,
        this.pursesFacade.purseID$,
        this.chooseLocationFacade.selectedLocation$
      ),
      switchMap(([action, discountTotal, subTotal, purseID, selectedLocation]) => {
        if (action.createSessionAction) {
          const sessionCreate = {
            amount: +subTotal - +discountTotal,
            intent: 'ClickAndCollectPay',
            purseID,
            locationID: selectedLocation ? selectedLocation.id : null,
          };
          return of(SessionCreateAction({ sessionCreate }));
        }
        return EMPTY;
      })
    )
  );

  protected sessionCreate$ = createEffect(() =>
    this.action$.pipe(
      ofType(SessionCreateAction),
      withLatestFrom(this.authenticationFacade.isAuthenticated$),
      switchMap(([sessionCreate, isAuthenticated]) => {
        if (isAuthenticated) {
          return this.orderService.sessionCreate(sessionCreate).pipe(
            map((paymentSessionCreate) => SessionCreateSuccessAction({ paymentSessionCreate })),
            catchError(() => {
              const failure = new AppStateFailureBuilder()
                .setFailureType(FailureType.API)
                .addFailure(new AppStateFailureItemWithErrorCode('Error on placing order', ApiResponseCode.Server_Error))
                .build();
              return of(SessionCreateErrorAction({ failure }));
            })
          );
        } else {
          return of(SessionCreateGuestCheckoutAction(sessionCreate));
        }
      })
    )
  );

  protected sessionCreateGuestCheckout$ = createEffect(() =>
    this.action$.pipe(
      ofType(SessionCreateGuestCheckoutAction),
      withLatestFrom(
        this.companyConfigFacade.checkoutAsGuestSharedAccountId$,
        this.orderFacade.guestCheckoutOrderID$,
        this.orderFacade.getDiscount$,
        this.orderFacade.discountTotal$,
        this.chooseLocationFacade.selectedLocation$,
        this.chooseLocationFacade.selectedTimeslot$,
        this.orderFacade.deliveryLocation$,
        this.orderFacade.childDeliveryLocation$,
        this.orderFacade.table$,
        this.cartFacade.cart$,
        this.cartFacade.subTotal$,
        this.orderFacade.customerInfo$,
        this.companyConfigFacade.orderFromSiteValue$,
        this.companyConfigFacade.isCustomerInfoOptional$,
        this.orderFacade.orderNotes$
      ),
      switchMap(
        ([
          action,
          checkoutAsGuestSharedAccountId,
          guestCheckoutOrderID,
          discounts,
          discountTotal,
          orderLocation,
          timeSlot,
          deliveryLocation,
          childDeliveryLocation,
          table,
          cart,
          subTotal,
          customerInfo,
          orderFromSiteValue,
          isCustomerInfoOptional,
          orderNotes,
        ]) => {
          let deliveryInfo = null;
          if (childDeliveryLocation) {
            deliveryInfo = { location: childDeliveryLocation.location, locationID: childDeliveryLocation.locationID } as Delivery;
          } else if (table) {
            deliveryInfo = {
              location: deliveryLocation.location,
              locationID: deliveryLocation.locationID,
              tableID: table.tableID,
              tableNo: table.tableNo,
            } as Delivery;
          }

          const grandTotal = (+subTotal - +discountTotal).toFixed(2);

          const orderInfo = new OrderInfoBuilder()
            .setOrderID(guestCheckoutOrderID)
            .setAccountID(checkoutAsGuestSharedAccountId)
            .setPeriodID(timeSlot.periodID)
            .setFirstName(customerInfo?.firstName ? customerInfo.firstName : '-')
            .setLastName(customerInfo?.lastName ? customerInfo.lastName : '-')
            .setEmail(customerInfo?.email ? customerInfo.email : '-')
            .setPhoneNumber(customerInfo?.phone ? customerInfo.phone : '-')
            .setLocationID(orderLocation.id)
            .setDiscounts(discounts)
            .setTimeslotStart(timeSlot.start)
            .setTimeslotEnd(timeSlot.end)
            .setDelivery(deliveryInfo)
            .setItems(cart)
            .setSubTotal(subTotal)
            .setTotal(+grandTotal)
            .setNotes(orderNotes)
            .build();

          return this.orderService.sessionCreateGuestCheckout(orderInfo).pipe(
            map((paymentSessionCreate) => SessionCreateSuccessAction({ paymentSessionCreate })),
            catchError(({ error }) => {
              let message = 'Error on placing order';
              if (error && error === 'InvalidTimeslot' && orderFromSiteValue && orderFromSiteValue === TimeslotSelection.Disabled) {
                return of(AutoUpdateTimeSlotIfExpiredAction({ reCreateSession: true }));
              } else if (error && error === 'InvalidTimeslot') {
                this.chooseLocationFacade.setTimeslotExpired(true);
                message = alerts.timeslot_has_expired_text;
              }

              const failure = new AppStateFailureBuilder()
                .setFailureType(FailureType.API)
                .addFailure(new AppStateFailureItemWithErrorCode(message, ApiResponseCode.Server_Error))
                .build();
              return of(SessionCreateGuestCheckoutErrorAction({ failure }));
            })
          );
        }
      )
    )
  );

  protected autoUpdateTimeSlotIfExpired$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(AutoUpdateTimeSlotIfExpiredAction),
        withLatestFrom(
          this.authenticationFacade.isAuthenticated$,
          this.chooseLocationFacade.selectedLocation$,
          this.companyConfigFacade.orderFromSiteValue$,
          this.cartFacade.subTotal$,
          this.orderFacade.discountTotal$,
          this.pursesFacade.purseID$
        ),
        switchMap(([action, isAuthenticated, selectedLocation, orderFromSiteValue, subTotal, discountTotal, purseID]) =>
          this.chooseLocationService.getLocationTimeslots({ locationID: selectedLocation.id, orderDate: new Date() }).pipe(
            map((orderingPeriods: any) => {
              const orderingPeriod = orderingPeriods[0];

              const currentTimeSlot = orderingPeriod.timeslots.find((timeslot) => timeslot.isOrderNow);
              if (currentTimeSlot) {
                if (orderFromSiteValue === TimeslotSelection.Disabled) {
                  this.chooseLocationFacade.setSelectedTimeslot({ ...currentTimeSlot, periodID: orderingPeriod.periodID });
                  this.orderFacade.applyDiscount(action.reCreateSession);
                } else {
                  const okButton = {
                    text: 'OK',
                    cssClass: 'okButton centerButton',
                    handler: () => {},
                  };
                  this.alertService.presentAlert('Alert', alerts.timeslot_has_expired_text, [okButton], false);
                  this.orderFacade.clearState();
                }
              } else {
                // we are closed alert
                this.alertService.presentAlert(alerts.alert_title_text, alerts.we_are_closed_text, [], false);
                this.orderFacade.clearState();
              }
              return EMPTY;
            }),
            catchError(() => {
              const failure = new AppStateFailureBuilder()
                .setFailureType(FailureType.API)
                .addFailure(new AppStateFailureItemWithErrorCode('Error on getting locationTimeslot', ApiResponseCode.Server_Error))
                .build();
              return of(getLocationTimeslotErrorAction({ failure }));
            })
          )
        )
      ),
    { dispatch: false }
  );

  protected sessionRetrieve$ = createEffect(() =>
    this.action$.pipe(
      ofType(SessionRetrieveAction),
      withLatestFrom(this.authenticationFacade.isAuthenticated$, this.orderFacade.getPaymentSessionCreate$),
      switchMap(([{ completePaymentInProcess }, isAuthenticated, paymentSession]) => {
        if (paymentSession) {
          if (isAuthenticated) {
            return this.orderService.sessionRetrieve({ sessionRetrieve: paymentSession.invoiceId }).pipe(
              map((paymentSessionRetrieve) => {
                return SessionRetrieveSuccessAction({ paymentSessionRetrieve, completePaymentInProcess });
              }),
              catchError(() => {
                const failure = new AppStateFailureBuilder()
                  .setFailureType(FailureType.API)
                  .addFailure(new AppStateFailureItemWithErrorCode('Error on getting order details', ApiResponseCode.Server_Error))
                  .build();
                return of(SessionRetrieveErrorAction({ failure }));
              })
            );
          } else {
            return of(SessionRetrieveGuestCheckoutAction({ sessionRetrieve: paymentSession.invoiceId }));
          }
        } else {
          return of(SessionRetrieveSuccessAction({ paymentSessionRetrieve: null }));
        }
      })
    )
  );

  protected sessionRetrieveGuestCheckout$ = createEffect(() =>
    this.action$.pipe(
      ofType(SessionRetrieveGuestCheckoutAction),
      withLatestFrom(this.orderFacade.guestCheckoutOrderID$),
      switchMap(([sessionRetrieve, guestCheckoutOrderID]) => {
        return this.orderService.sessionRetrieveGuestCheckout(sessionRetrieve, guestCheckoutOrderID).pipe(
          map((paymentSessionRetrieve) => {
            return SessionRetrieveSuccessAction({ paymentSessionRetrieve });
          }),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on getting order details', ApiResponseCode.Server_Error))
              .build();
            return of(SessionRetrieveErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected orderDetails$ = createEffect(() =>
    this.action$.pipe(
      ofType(OrderDetailsAction),
      switchMap((OrderId) => {
        return this.orderService.getOrderDetails(OrderId).pipe(
          map((orderDetails) => {
            return OrderDetailsSuccessAction({ orderDetails });
          }),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on getting order details', ApiResponseCode.Server_Error))
              .build();
            return of(OrderDetailsErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected checkOrderStatus$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(checkOrderStatusAction),
        withLatestFrom(
          this.orderFacade.orderID$,
          this.orderFacade.getPaymentSessionCreate$,
          this.authenticationFacade.isAuthenticated$,
          this.orderFacade.paymentSessionIntent$,
          this.orderFacade.guestCheckoutOrderID$,
          this.companyConfigFacade.isGoogleAndApplePayAllowed$
        ),
        switchMap(
          ([{ id }, orderID, paymentSession, isAuthenticated, paymentSessionIntent, guestCheckoutOrderID, isGoogleAndApplePayAllowed]) => {
            const PAYMENT_SESSION_INTENT = 'ClickAndCollectPay';
            const ORDER_ID = id ? id : orderID;
            const order = { id: isAuthenticated ? ORDER_ID : guestCheckoutOrderID };

            if (isGoogleAndApplePayAllowed && paymentSession && order.id > 0 && paymentSessionIntent === PAYMENT_SESSION_INTENT) {
              if (isAuthenticated) {
                return this.orderService.getOrderDetails(order).pipe(
                  map((orderDetails) => {
                    if (orderDetails && orderDetails.status === 'Draft') {
                      this.orderFacade.sessionRetrieve(true);
                    } else if (orderDetails && orderDetails.status !== 'Draft') {
                      this.orderFacade.setOrderCompletedOn(new Date().toISOString());
                      this.navCtrl.navigateForward(`${PAYMENT_SUCCESS}?id=${paymentSession.invoiceId}&orderno=${orderDetails.orderID}`);
                    }
                    return of(null);
                  }),
                  catchError((error) => {
                    const failure = new AppStateFailureBuilder()
                      .setFailureType(FailureType.API)
                      .addFailure(new AppStateFailureItemWithErrorCode('Error on getting order details', ApiResponseCode.Server_Error))
                      .build();
                    return of(OrderDetailsErrorAction({ failure }));
                  })
                );
              } else {
                this.orderFacade.sessionRetrieve(true);
                return of(null);
              }
            } else {
              return of(null);
            }
          }
        )
      ),
    { dispatch: false }
  );

  protected orderCompleted$ = createEffect(() =>
    this.action$.pipe(
      ofType(OrderCompleteAction),
      switchMap(() => {
        return this.orderService.orderComplete().pipe(
          tap((orderId) => {
            if (orderId) {
              this.navCtrl.navigateForward(`${PAYMENT_SUCCESS}?orderId=${orderId}`);
            }
          }),
          map((orderId) => OrderCompleteSuccessAction(orderId)),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on order completion', ApiResponseCode.Server_Error))
              .build();
            return of(OrderCompleteErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected showGuestCheckoutAlert$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(showGuestCheckoutAlert),
        withLatestFrom(this.companyConfigFacade.isAnonymousBrowsing$, this.companyConfigFacade.checkoutAsGuestSharedAccountId$),
        tap(([action, isAnonymousBrowsing, checkoutAsGuestSharedAccountId]) => {
          // need to check a valid payment gateway to show alert
          if (isAnonymousBrowsing && checkoutAsGuestSharedAccountId.length > 0) {
            this.alertService.guestCheckoutAlert();
          } else if (isAnonymousBrowsing) {
            this.alertService.loginSignupAlert();
          }
        })
      ),
    { dispatch: false }
  );

  protected sessionRetrieveSuccess$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(SessionRetrieveSuccessAction),
        withLatestFrom(
          this.orderFacade.cardPaymentInProcess$,
          this.orderFacade.getPaymentSessionCreate$,
          this.orderFacade.getPaymentSessionRetrieve$
        ),
        tap(([action, cardPaymentInProcess, paymentSession, paymentSessionRetrieve]) => {
          if (cardPaymentInProcess) {
            if (paymentSessionRetrieve.intent === 'Topup') {
              if (paymentSessionRetrieve && paymentSessionRetrieve.success && paymentSessionRetrieve.completed) {
                this.modalCtrl
                  .create({
                    component: WalletInterrogateSuccessPageComponent,
                    componentProps: {
                      topup: paymentSessionRetrieve.amount,
                    },
                    showBackdrop: true,
                    mode: 'ios',
                    cssClass: 'alert-modal base_modal',
                  })
                  .then((modal) => {
                    modal.present();
                    modal.onDidDismiss().then(() => {
                      this.pursesFacade.getPurses();
                      this.orderFacade.clearState('paymentSessionIntent');
                    });
                  });
              } else {
                this.orderFacade.clearState('createSession');
                this.navCtrl.navigateForward(LOYALTY_WALLET_FULL_TABS);
              }
            } else {
              if (paymentSessionRetrieve && paymentSessionRetrieve.success && paymentSessionRetrieve.completed) {
                this.orderFacade.setOrderCompletedOn(new Date().toISOString());
                this.navCtrl.navigateForward(`${PAYMENT_SUCCESS}?id=${paymentSession.invoiceId}`);
              } else {
                this.orderFacade.clearState('createSession');
                this.navCtrl.navigateForward(CHECKOUT_ORDER);
              }
            }
          }
        })
      ),
    { dispatch: false }
  );

  protected checkTopUpStatus$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(checkTopUpStatusAction),
        withLatestFrom(
          this.orderFacade.getPaymentSessionCreate$,
          this.authenticationFacade.isAuthenticated$,
          this.orderFacade.paymentSessionIntent$,
          this.companyConfigFacade.isGoogleAndApplePayAllowed$
        ),
        switchMap(([{ id }, paymentSession, isAuthenticated, paymentSessionIntent, isGoogleAndApplePayAllowed]) => {
          const PAYMENT_SESSION_INTENT = 'Topup';
          if (isGoogleAndApplePayAllowed && isAuthenticated && paymentSession && paymentSessionIntent === PAYMENT_SESSION_INTENT) {
            return this.orderService.sessionRetrieve({ sessionRetrieve: paymentSession.invoiceId }).pipe(
              map((paymentSessionRetrieve) => {
                const completePaymentInProcess = true;
                return SessionRetrieveSuccessAction({ paymentSessionRetrieve, completePaymentInProcess });
              }),
              catchError(() => {
                const failure = new AppStateFailureBuilder()
                  .setFailureType(FailureType.API)
                  .addFailure(new AppStateFailureItemWithErrorCode('Error on getting order details', ApiResponseCode.Server_Error))
                  .build();
                return of(SessionRetrieveErrorAction({ failure }));
              })
            );
          } else {
            return EMPTY;
          }
        })
      ),
    { dispatch: true }
  );
}
